import { useMemo, useState } from "react";
import Modal from "../LancarComissao/Modal";
import "./ListaUsuarios.css";
import Api from "../../Services/Api";
import {
  formatLedgerStatementDate,
  getLedgerStatementDateKey,
} from "../../../utils/ledgerEntryDate";
import { filterLicensedLedgerEntries } from "../../../utils/licensedLedgerVisibility";
import {
  ACCESS_TYPES,
  getAccessTypeLabel,
  isSuperAdmin,
  normalizeAccessType,
} from "../../../utils/accessControl";

const onlyDigits = (value = "") => String(value || "").replace(/\D/g, "");

const formatCpfCnpj = (value = "") => {
  const digits = onlyDigits(value);

  if (!digits) return "-";

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4")
      .slice(0, 14);
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5")
    .slice(0, 18);
};

const formatAccountNumber = (bankData) => {
  const accountNumber = String(bankData?.account_number || "").trim();
  const accountDigit = String(bankData?.account_digit || "").trim();

  if (!accountNumber) return "-";
  return accountDigit ? `${accountNumber}-${accountDigit}` : accountNumber;
};

const formatCurrencyBRL = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatClientDisplayName = (value, maxLength = 48) => {
  const text = String(value || "").trim();
  if (!text) return "-";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
};

const INITIAL_EXTRATO_FILTERS = {
  startDate: "",
  endDate: "",
  type: "ALL",
  status: "ALL",
};

const getLedgerEntryType = (entry) => {
  const origin = String(entry?.origin || "").trim().toUpperCase();
  const entryType = String(entry?.entry_type || "").trim().toUpperCase();

  if (origin === "WITHDRAW" || entryType === "DEBIT") {
    return "DEBIT";
  }

  return "CREDIT";
};

const getLedgerPurchaseAmount = (entry) => Number(entry?.purchase_amount) || 0;

const getLedgerCommissionValue = (entry) => {
  const purchaseAmount = getLedgerPurchaseAmount(entry);
  const commissionPercent = Number(entry?.commission_percent) || 0;

  if (getLedgerEntryType(entry) === "DEBIT") {
    return -purchaseAmount;
  }

  return (purchaseAmount * commissionPercent) / 100;
};

const normalizeLedgerStatus = (status) => {
  const normalizedStatus = String(status || "").trim().toUpperCase();

  if (normalizedStatus === "REQUESTED") {
    return "WITHDRAW_REQUESTED";
  }

  if (normalizedStatus === "CANCELLED") {
    return "CANCELED";
  }

  return normalizedStatus;
};

const renderLedgerStatusLabel = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "CONFIRMED":
      return { label: "Confirmado", className: "status-pill status-credito" };
    case "PENDING":
      return { label: "Pendente", className: "status-pill status-analise" };
    case "AWAITING_CONFIRMATION":
      return {
        label: "Aguardando confirmação",
        className: "status-pill status-aguardando-confirmacao",
      };
    case "WITHDRAW_REQUESTED":
    case "REQUESTED":
      return { label: "Saque solicitado", className: "status-pill status-saque-solicitado" };
    case "CANCELED":
    case "CANCELLED":
      return { label: "Cancelado", className: "status-pill" };
    default:
      return { label: "-", className: "status-pill" };
  }
};

const ListaUsuarios = ({ lista = [], viewerRole, onEdit, onToggleStatus }) => {
  const [confirmacao, setConfirmacao] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [extratoModal, setExtratoModal] = useState({
    open: false,
    loading: false,
    error: "",
    user: null,
    balance: null,
    data: [],
  });
  const [dadosBancariosModal, setDadosBancariosModal] = useState({
    open: false,
    loading: false,
    error: "",
    user: null,
    data: null,
  });
  const [carregandoExtratoId, setCarregandoExtratoId] = useState(null);
  const [carregandoBancariosId, setCarregandoBancariosId] = useState(null);
  const [extratoFiltros, setExtratoFiltros] = useState(INITIAL_EXTRATO_FILTERS);

  const viewerIsSuperAdmin = isSuperAdmin(viewerRole);
  const itens = lista;

  const textoContexto = viewerIsSuperAdmin
    ? "Super administrador pode cadastrar, editar e visualizar todos os usuarios, incluindo administradores e super administradores."
    : "Admin pode cadastrar e editar licenciados, alem de acessar extrato e dados bancarios de cada usuario.";

  const tituloNome = viewerIsSuperAdmin ? "Nome do usuario" : "Nome do licenciado";
  const rodapeTexto = `${itens.length} ${
    viewerIsSuperAdmin ? "usuarios" : "licenciados"
  } encontrados`;

  const renderizarStatus = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "ACTIVE":
        return <span className="status-pill status-ativo">Ativo</span>;
      case "INACTIVE":
        return <span className="status-pill status-inativo">Inativo</span>;
      default:
        return <span className="status-pill">-</span>;
    }
  };

  const formatarDataBR = (data) => {
    if (!data) return "-";
    return new Intl.DateTimeFormat("pt-BR").format(new Date(data));
  };

  const getRoleBadgeClass = (role) => {
    switch (normalizeAccessType(role)) {
      case ACCESS_TYPES.SUPER_ADMIN:
        return "tag-role tag-role-super-admin";
      case ACCESS_TYPES.ADMIN:
        return "tag-role tag-role-admin";
      default:
        return "tag-role tag-role-licensed";
    }
  };

  const abrirConfirmacao = (item) => {
    const statusAtual = String(item?.status || "").toUpperCase();
    const proximoStatus = statusAtual === "INACTIVE" ? "ACTIVE" : "INACTIVE";
    setConfirmacao({ item, proximoStatus });
  };

  const fecharConfirmacao = () => {
    if (processando) return;
    setConfirmacao(null);
  };

  const fecharExtrato = () => {
    setExtratoFiltros(INITIAL_EXTRATO_FILTERS);
    setExtratoModal({
      open: false,
      loading: false,
      error: "",
      user: null,
      balance: null,
      data: [],
    });
    setCarregandoExtratoId(null);
  };

  const fecharDadosBancarios = () => {
    setDadosBancariosModal({
      open: false,
      loading: false,
      error: "",
      user: null,
      data: null,
    });
    setCarregandoBancariosId(null);
  };

  const confirmarTrocaStatus = async () => {
    if (!confirmacao?.item || !onToggleStatus) return;

    try {
      setProcessando(true);
      await onToggleStatus(confirmacao.item);
      setConfirmacao(null);
    } catch (err) {
      alert(err?.message || "Nao foi possivel atualizar o status.");
    } finally {
      setProcessando(false);
    }
  };

  const abrirDadosBancarios = async (item) => {
    if (!item?.id) return;

    setCarregandoBancariosId(item.id);
    setDadosBancariosModal({
      open: true,
      loading: true,
      error: "",
      user: item,
      data: null,
    });

    try {
      const res = await Api.get(`/bank-accounts/user/${item.id}`);
      const payload = res.data || {};

      setDadosBancariosModal({
        open: true,
        loading: false,
        error: "",
        user: payload.user || item,
        data: payload.data ?? null,
      });
    } catch (err) {
      setDadosBancariosModal({
        open: true,
        loading: false,
        error:
          err?.response?.data?.message ||
          "Nao foi possivel carregar os dados bancarios deste usuario.",
        user: item,
        data: null,
      });
    } finally {
      setCarregandoBancariosId(null);
    }
  };

  const abrirExtrato = async (item) => {
    if (!item?.id) return;

    setExtratoFiltros(INITIAL_EXTRATO_FILTERS);
    setCarregandoExtratoId(item.id);
    setExtratoModal({
      open: true,
      loading: true,
      error: "",
      user: item,
      balance: null,
      data: [],
    });

    try {
      const [extratoRes, balanceRes] = await Promise.all([
        Api.get("/ledger-entries", {
          params: {
            user_id: item.id,
            limit: 200,
          },
        }),
        Api.get(`/ledger-entries/user/${item.id}/balance`),
      ]);

      const balancePayload = balanceRes.data?.data ?? balanceRes.data ?? null;
      const balanceUser = balanceRes.data?.user ?? item;

      setExtratoModal({
        open: true,
        loading: false,
        error: "",
        user: balanceUser,
        balance: balancePayload,
        data: Array.isArray(extratoRes.data) ? extratoRes.data : [],
      });
    } catch (err) {
      setExtratoModal({
        open: true,
        loading: false,
        error:
          err?.response?.data?.message || "Nao foi possivel carregar o extrato deste usuario.",
        user: item,
        balance: null,
        data: [],
      });
    } finally {
      setCarregandoExtratoId(null);
    }
  };

  const linhas = useMemo(
    () =>
      itens.map((item) => {
        const accessType = normalizeAccessType(item?.access_type);
        const isInativo = String(item.status || "").toUpperCase() === "INACTIVE";
        const isLicensed = accessType === ACCESS_TYPES.LICENSED;

        return {
          ...item,
          accessType,
          isInativo,
          isLicensed,
          roleLabel: getAccessTypeLabel(accessType),
        };
      }),
    [itens]
  );

  const conta = dadosBancariosModal.data;
  const bancoNome = conta?.bank_name || conta?.bank_code || "-";
  const agencia = conta?.branch || "-";
  const contaFormatada = formatAccountNumber(conta);
  const titular = conta?.account_holder_name || "-";
  const documentoTitular = formatCpfCnpj(conta?.account_holder_cpf_cnpj);
  const tipoConta = conta?.account_type || "-";
  const pix = conta?.pix_key || "-";
  const extratoItens = filterLicensedLedgerEntries(extratoModal.data);
  const extratoBalance = extratoModal.balance;
  const saldoDisponivelExtrato = Number(extratoBalance?.saldo_total_disponivel || 0);
  const creditosConfirmadosExtrato = Number(extratoBalance?.creditos_confirmados || 0);
  const limiteSaqueExtrato = Number(extratoBalance?.limite_saque || 0);
  const saquesSolicitadosExtrato = Number(extratoBalance?.saques_solicitados || 0);
  const extratoItensFiltrados = useMemo(() => {
    return extratoItens.filter((entry) => {
      const dataLancamento = getLedgerStatementDateKey(entry);
      const tipoLancamento = getLedgerEntryType(entry);
      const statusLancamento = normalizeLedgerStatus(entry?.status);

      if (extratoFiltros.startDate && (!dataLancamento || dataLancamento < extratoFiltros.startDate)) {
        return false;
      }

      if (extratoFiltros.endDate && (!dataLancamento || dataLancamento > extratoFiltros.endDate)) {
        return false;
      }

      if (extratoFiltros.type !== "ALL" && tipoLancamento !== extratoFiltros.type) {
        return false;
      }

      if (
        extratoFiltros.status !== "ALL" &&
        statusLancamento !== extratoFiltros.status
      ) {
        return false;
      }

      return true;
    });
  }, [extratoFiltros, extratoItens]);
  const totalValorCompraExtrato = extratoItensFiltrados.reduce(
    (acc, entry) => acc + getLedgerPurchaseAmount(entry),
    0
  );
  const totalValorComissaoExtrato = extratoItensFiltrados.reduce(
    (acc, entry) => acc + getLedgerCommissionValue(entry),
    0
  );

  return (
    <section className="table-card">
      <div className="title-list">{textoContexto}</div>

      <div className="tablela">
        <table>
          <thead>
            <tr>
              <th>{tituloNome}</th>
              {viewerIsSuperAdmin ? <th>Perfil</th> : null}
              <th>E-mail de acesso</th>
              <th>Status</th>
              <th>Data de criacao</th>
              <th>Acoes</th>
            </tr>
          </thead>

          <tbody id="usuariosTableBody">
            {linhas.map((item) => (
              <tr key={item.id} className={item.isInativo ? "row-inactive" : ""}>
                <td>{item.nome}</td>
                {viewerIsSuperAdmin ? (
                  <td>
                    <span className={getRoleBadgeClass(item.accessType)}>
                      {item.roleLabel}
                    </span>
                  </td>
                ) : null}
                <td>{item.email}</td>
                <td>{renderizarStatus(item.status)}</td>
                <td>{formatarDataBR(item.dataCriacao)}</td>
                <td>
                  <div className="actions-cell">
                    <button
                      className="action-btn"
                      data-id={item.id}
                      data-action="editar"
                      onClick={() => onEdit?.(item.id)}
                    >
                      <span>Editar</span>
                    </button>

                    {item.isLicensed ? (
                      <>
                        <button
                          className="action-btn"
                          data-id={item.id}
                          data-action="extrato"
                          onClick={() => abrirExtrato(item)}
                          disabled={carregandoExtratoId === item.id}
                        >
                          <span>
                            {carregandoExtratoId === item.id ? "Carregando..." : "Ver extrato"}
                          </span>
                        </button>

                        <button
                          className="action-btn"
                          data-id={item.id}
                          data-action="bancarios"
                          onClick={() => abrirDadosBancarios(item)}
                          disabled={carregandoBancariosId === item.id}
                        >
                          <span>
                            {carregandoBancariosId === item.id
                              ? "Carregando..."
                              : "Dados bancarios"}
                          </span>
                        </button>
                      </>
                    ) : null}

                    <button
                      className={`action-btn ${
                        item.isInativo ? "action-btn-success" : "action-btn-danger"
                      }`}
                      data-id={item.id}
                      data-action={item.isInativo ? "reativar" : "excluir"}
                      onClick={() => abrirConfirmacao(item)}
                    >
                      <span>{item.isInativo ? "Reativar" : "Excluir"}</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div id="usuariosInfo">{rodapeTexto}</div>
      </div>

      <Modal
        open={Boolean(confirmacao)}
        title={confirmacao?.proximoStatus === "INACTIVE" ? "Confirmar exclusao" : "Confirmar reativacao"}
        onClose={fecharConfirmacao}
      >
        <div className="confirm-status-content">
          {confirmacao?.proximoStatus === "INACTIVE" ? (
            <p>
              O usuario <strong>{confirmacao?.item?.nome}</strong> sera desativado. O cadastro nao sera apagado, ficara
              riscado na lista e nao podera fazer login ate ser reativado.
            </p>
          ) : (
            <p>
              O usuario <strong>{confirmacao?.item?.nome}</strong> sera reativado e voltara a poder acessar a conta.
            </p>
          )}

          <div className="confirm-status-actions">
            <button type="button" className="action-btn" onClick={fecharConfirmacao} disabled={processando}>
              Cancelar
            </button>
            <button
              type="button"
              className={`action-btn ${
                confirmacao?.proximoStatus === "INACTIVE"
                  ? "action-btn-danger"
                  : "action-btn-success"
              }`}
              onClick={confirmarTrocaStatus}
              disabled={processando}
            >
              {processando
                ? "Processando..."
                : confirmacao?.proximoStatus === "INACTIVE"
                ? "Confirmar exclusao"
                : "Confirmar reativacao"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={extratoModal.open} title="Extrato do licenciado" onClose={fecharExtrato}>
        <div className="bank-modal-content">
          <div className="bank-modal-header">
            <strong>{extratoModal.user?.nome || "Usuário"}</strong>
            <span>{extratoModal.user?.email || "-"}</span>
          </div>

          {extratoModal.loading ? (
            <p className="bank-modal-state">Carregando extrato...</p>
          ) : extratoModal.error ? (
            <p className="bank-modal-state bank-modal-state-error">{extratoModal.error}</p>
          ) : (
            <div className="statement-modal-content">
              {extratoBalance ? (
                <div className="statement-balance-card">
                  <div className="statement-balance-label">Valor disponível</div>
                  <div className="statement-balance-value">
                    {formatCurrencyBRL(saldoDisponivelExtrato)}
                  </div>
                  <div className="statement-balance-breakdown">
                    <span>Créditos confirmados: {formatCurrencyBRL(creditosConfirmadosExtrato)}</span>
                    <span>Limite disponível: {formatCurrencyBRL(limiteSaqueExtrato)}</span>
                    <span>Saques solicitados: {formatCurrencyBRL(saquesSolicitadosExtrato)}</span>
                  </div>
                </div>
              ) : null}

              <div className="statement-filters-card">
                <div className="statement-filters-header">
                  <strong>Filtros</strong>
                  <button
                    type="button"
                    className="action-btn"
                    onClick={() => setExtratoFiltros(INITIAL_EXTRATO_FILTERS)}
                  >
                    Limpar filtros
                  </button>
                </div>

                <div className="statement-filters-grid">
                  <label className="statement-filter-field">
                    <span>Data inicial</span>
                    <input
                      type="date"
                      value={extratoFiltros.startDate}
                      onChange={(e) =>
                        setExtratoFiltros((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label className="statement-filter-field">
                    <span>Data final</span>
                    <input
                      type="date"
                      value={extratoFiltros.endDate}
                      onChange={(e) =>
                        setExtratoFiltros((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label className="statement-filter-field">
                    <span>Tipo</span>
                    <select
                      value={extratoFiltros.type}
                      onChange={(e) =>
                        setExtratoFiltros((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                    >
                      <option value="ALL">Todos</option>
                      <option value="CREDIT">Crédito</option>
                      <option value="DEBIT">Débito</option>
                    </select>
                  </label>

                  <label className="statement-filter-field">
                    <span>Status</span>
                    <select
                      value={extratoFiltros.status}
                      onChange={(e) =>
                        setExtratoFiltros((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      <option value="ALL">Todos</option>
                      <option value="CONFIRMED">Confirmado</option>
                      <option value="PENDING">Pendente</option>
                      <option value="AWAITING_CONFIRMATION">Aguardando confirmação</option>
                      <option value="WITHDRAW_REQUESTED">Saque solicitado</option>
                      <option value="CANCELED">Cancelado</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="statement-modal-summary">
                Exibindo {extratoItensFiltrados.length} de {extratoItens.length} lançamento
                {extratoItensFiltrados.length !== 1 ? "s" : ""}.
              </div>

              {extratoItens.length === 0 ? (
                <div className="bank-modal-empty">
                  <p>Este licenciado ainda não possui lançamentos visíveis no extrato.</p>
                </div>
              ) : extratoItensFiltrados.length === 0 ? (
                <div className="bank-modal-empty">
                  <p>Nenhum lançamento encontrado para os filtros selecionados.</p>
                </div>
              ) : (
                <div className="statement-table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Nº compra</th>
                        <th>Cliente</th>
                        <th>Valor compra</th>
                        <th>Valor comissão</th>
                        <th>Tipo</th>
                        <th>Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {extratoItensFiltrados.map((entry) => {
                        const isWithdraw = getLedgerEntryType(entry) === "DEBIT";
                        const purchaseAmount = getLedgerPurchaseAmount(entry);
                        const commissionValue = getLedgerCommissionValue(entry);
                        const statusInfo = renderLedgerStatusLabel(entry?.status);
                        const clienteCompleto = String(entry?.client_name || "").trim() || "-";
                        const clienteExibido = formatClientDisplayName(clienteCompleto);

                        return (
                          <tr key={entry.id}>
                            <td>{formatLedgerStatementDate(entry)}</td>
                            <td>{entry?.order_number || "-"}</td>
                            <td className="statement-client-cell" title={clienteCompleto}>
                              <span className="statement-client-text">{clienteExibido}</span>
                            </td>
                            <td>{formatCurrencyBRL(purchaseAmount)}</td>
                            <td>{formatCurrencyBRL(commissionValue)}</td>
                            <td className={isWithdraw ? "tipo-debito" : "tipo-credito"}>
                              {isWithdraw ? "Débito" : "Crédito"}
                            </td>
                            <td>
                              <span className={statusInfo.className}>{statusInfo.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>

                    <tfoot>
                      <tr className="statement-table-total-row">
                        <td colSpan={3}>Totais do filtro</td>
                        <td>{formatCurrencyBRL(totalValorCompraExtrato)}</td>
                        <td>{formatCurrencyBRL(totalValorComissaoExtrato)}</td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="bank-modal-actions">
            <button type="button" className="action-btn" onClick={fecharExtrato}>
              Fechar
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={dadosBancariosModal.open}
        title="Dados bancários"
        onClose={fecharDadosBancarios}
      >
        <div className="bank-modal-content">
          <div className="bank-modal-header">
            <strong>{dadosBancariosModal.user?.nome || "Usuário"}</strong>
            <span>{dadosBancariosModal.user?.email || "-"}</span>
          </div>

          {dadosBancariosModal.loading ? (
            <p className="bank-modal-state">Carregando dados bancários...</p>
          ) : dadosBancariosModal.error ? (
            <p className="bank-modal-state bank-modal-state-error">
              {dadosBancariosModal.error}
            </p>
          ) : !conta ? (
            <div className="bank-modal-empty">
              <p>Este licenciado ainda não possui dados bancários cadastrados.</p>
            </div>
          ) : (
            <div className="bank-details-grid">
              <div className="bank-detail-item">
                <span className="bank-detail-label">Titular</span>
                <strong>{titular}</strong>
              </div>

              <div className="bank-detail-item">
                <span className="bank-detail-label">CPF/CNPJ</span>
                <strong>{documentoTitular}</strong>
              </div>

              <div className="bank-detail-item">
                <span className="bank-detail-label">Banco</span>
                <strong>{bancoNome}</strong>
              </div>

              <div className="bank-detail-item">
                <span className="bank-detail-label">Tipo de conta</span>
                <strong>{tipoConta}</strong>
              </div>

              <div className="bank-detail-item">
                <span className="bank-detail-label">Agência</span>
                <strong>{agencia}</strong>
              </div>

              <div className="bank-detail-item">
                <span className="bank-detail-label">Conta</span>
                <strong>{contaFormatada}</strong>
              </div>

              <div className="bank-detail-item bank-detail-item-full">
                <span className="bank-detail-label">Chave Pix</span>
                <strong>{pix}</strong>
              </div>
            </div>
          )}

          <div className="bank-modal-actions">
            <button type="button" className="action-btn" onClick={fecharDadosBancarios}>
              Fechar
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default ListaUsuarios;
